"use client"

import { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import styled from "styled-components"
import { motion, AnimatePresence } from "framer-motion"
import { FiMenu, FiX, FiHome, FiUser, FiFolder, FiAward, FiMail } from "react-icons/fi"
import { useApp } from "../contexts/AppContext"

const NavContainer = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.fixed};
  transition: all ${({ theme }) => theme.transitions.normal};
  background: ${({ $scrolled, theme }) => ($scrolled ? `rgba(0, 0, 0, 0.9)` : "transparent")};
  backdrop-filter: ${({ $scrolled }) => ($scrolled ? "blur(20px)" : "none")};
  border-bottom: ${({ $scrolled, theme }) => ($scrolled ? `1px solid ${theme.colors.border}` : "none")};
  box-shadow: ${({ $scrolled, theme }) => ($scrolled ? theme.shadows.lg : "none")};
`

const NavContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 ${({ theme }) => theme.spacing.sm};
  }
`

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    transform: scale(1.05);
  }
`

const LogoIcon = styled(motion.div)`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.gradientPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.background};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  box-shadow: ${({ theme }) => theme.shadows.glow};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    z-index: -1;
    filter: blur(8px);
    opacity: 0.6;
  }
`

const LogoText = styled(motion.span)`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.fonts.secondary};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`

const DesktopNav = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ $active, theme }) => ($active ? theme.colors.background : theme.colors.textSecondary)};
  background: ${({ $active, theme }) => ($active ? theme.colors.gradientPrimary : "transparent")};
  transition: all ${({ theme }) => theme.transitions.normal};
  text-decoration: none;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    transition: left 0.3s ease;
    z-index: -1;
    border-radius: ${({ theme }) => theme.borderRadius.xl};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.background};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.glow};

    &::before {
      left: 0;
    }
  }
`

const MobileMenuButton = styled(motion.button)`
  display: none;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.background};
    box-shadow: ${({ theme }) => theme.shadows.glow};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
  }
`

const MobileMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: ${({ theme }) => theme.spacing.md};
  right: ${({ theme }) => theme.spacing.md};
  background: rgba(26, 26, 26, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius["2xl"]};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows["2xl"]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    left: ${({ theme }) => theme.spacing.sm};
    right: ${({ theme }) => theme.spacing.sm};
  }
`

const MobileNavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ $active, theme }) => ($active ? theme.colors.background : theme.colors.textSecondary)};
  background: ${({ $active, theme }) => ($active ? theme.colors.gradientPrimary : "transparent")};
  transition: all ${({ theme }) => theme.transitions.normal};
  text-decoration: none;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.gradientPrimary};
    transition: left 0.3s ease;
    z-index: -1;
    border-radius: ${({ theme }) => theme.borderRadius.xl};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.background};

    &::before {
      left: 0;
    }
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false)
  const { isMenuOpen, setIsMenuOpen } = useApp()
  const location = useLocation()

  const navItems = [
    { name: "Home", path: "/", icon: FiHome },
    { name: "About", path: "/about", icon: FiUser },
    { name: "Projects", path: "/projects", icon: FiFolder },
    { name: "Certificates", path: "/certificates", icon: FiAward },
    { name: "Contact", path: "/contact", icon: FiMail },
  ]

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen)

  return (
    <NavContainer
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
      $scrolled={scrolled}
    >
      <NavContent>
        <Logo to="/">
          <LogoIcon whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
            AN
          </LogoIcon>
          <LogoText
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Aline NIYONIZERA
          </LogoText>
        </Logo>

        <DesktopNav>
          {navItems.map((item, index) => {
            const Icon = item.icon
            return (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index, duration: 0.6 }}
              >
                <NavLink to={item.path} $active={location.pathname === item.path}>
                  <Icon size={18} />
                  <span>{item.name}</span>
                </NavLink>
              </motion.div>
            )
          })}
        </DesktopNav>

        <MobileMenuButton onClick={toggleMenu} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
          <AnimatePresence mode="wait">
            {isMenuOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <FiX size={24} />
              </motion.div>
            ) : (
              <motion.div
                key="menu"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <FiMenu size={24} />
              </motion.div>
            )}
          </AnimatePresence>
        </MobileMenuButton>

        <AnimatePresence>
          {isMenuOpen && (
            <MobileMenu
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3, type: "spring", stiffness: 300 }}
            >
              {navItems.map((item, index) => {
                const Icon = item.icon
                return (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * index, duration: 0.3 }}
                  >
                    <MobileNavLink
                      to={item.path}
                      $active={location.pathname === item.path}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Icon size={20} />
                      <span>{item.name}</span>
                    </MobileNavLink>
                  </motion.div>
                )
              })}
            </MobileMenu>
          )}
        </AnimatePresence>
      </NavContent>
    </NavContainer>
  )
}

export default Navbar
